import clsx from 'clsx'
import { Col, Grid, Icon, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TRANSACTION_CONFIG } from 'modules/Finance/Wallets/config/config'
import styles from 'modules/Finance/Wallets/subcomponents/Transaction/Transaction.module.scss'
import formatMoney from 'utils/formatMoney'

const TransactionRow = ({
  amount,
  type = null,
  currency = null,
  className = '',
  document: {
    status = null,
    id = null,
    account = null,
    type: documentType,
    invoice_number = null,
  } = {},
}) => {
  const { t } = useTranslation()

  const heading = useMemo(
    () =>
      `${
        type
          ? t(`wallets::transaction.${type}.${amount > 0 ? 'in' : 'out'}`, {
              accountLogin: account?.login ?? '',
              accountType: t(`common::accountType.${account?.type ?? ''}`),
              documentType: documentType ? t(`common::documentType.${documentType}`) : '',
              id,
            })
          : ''
      }${
        !['ad_tax_hold_withdrawal', 'ad_tax_hold_deposit'].includes(type) && invoice_number
          ? ` № ${invoice_number}`
          : ''
      }`,
    [type, amount, account, documentType, t],
  )

  const action = useMemo(() => {
    return `${type ? t(`wallets::transaction.operation`) : ''} ${id || ''} ${
      status ? t(`wallets::transaction.status.${status}`) : ''
    }`
  }, [type, id, status, t])

  const isPositive = amount >= 0
  const direction = isPositive ? 'in' : 'out'

  const {
    bgColor = 'var(--color-Background-Primary)',
    color = 'var(--color-Label-Onsurface)',
    name = '',
  } = TRANSACTION_CONFIG?.[type]?.[direction] ?? {}

  return (
    <Grid
      alignItems={'center'}
      className={className}
      wrap={false}
      gap={16}
    >
      <Col
        flexShrink={0}
        flexGrow={0}
        className={clsx(styles['transaction__col'], styles['transaction__col_badge'])}
      >
        <Grid
          alignItems={'center'}
          justifyContent={'center'}
          className={clsx(styles['transaction__badge'])}
          style={{ backgroundColor: bgColor }}
        >
          <Icon
            width={20}
            name={name}
            style={{ color: color }}
          />
        </Grid>
      </Col>
      <Col
        flexShrink={1}
        flexGrow={1}
        className={styles['transaction__col']}
      >
        <Grid
          fluid={false}
          direction={'column'}
          gap={4}
          className={styles['transaction__col']}
        >
          <Typography
            variant={'Body-15'}
            color={'Label-Fifth'}
          >
            {heading}
          </Typography>
          <Typography
            variant={'Body-10'}
            color={'Label-Secondary'}
          >
            {action}
          </Typography>
        </Grid>
      </Col>
      <Col className={styles['transaction__col']}>
        <Typography
          style={{ display: 'inline-block' }}
          variant={'Body-20'}
          color={amount > 0 ? 'Label-Confirm' : 'Label-Fifth'}
          noWrap
          component={'div'}
          dangerouslySetInnerHTML={{
            __html: (amount > 0 ? '+' : '') + formatMoney(amount, currency),
          }}
        />
      </Col>
    </Grid>
  )
}

export default TransactionRow

TransactionRow.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currency: PropTypes.string,
  action: PropTypes.string,
  heading: PropTypes.string,
  status: PropTypes.oneOf(['pending', 'success', 'declined', 'cancelled', 'error']),
  type: PropTypes.string,
  created_at: PropTypes.string,
  document: PropTypes.shape({
    status: PropTypes.string,
    id: PropTypes.number,
    account: PropTypes.shape({
      login: PropTypes.string,
      type: PropTypes.string,
    }),
    type: PropTypes.string,
  }),
  className: PropTypes.string,
}
